import React, { useContext, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuBookOpen, LuLayers, LuRefreshCw, LuSparkles, LuZap } from 'react-icons/lu';
import AuthContext from '../../components/shared/AuthContext';
import PageMeta from '../../components/seo/PageMeta';
import AdaptivePageShell from '../../features/adaptive-learning/AdaptivePageShell';
import { useTodayPlan } from '../../features/adaptive-learning/useAdaptiveLearning';
import { TIME_PRESETS, resolveLearningLink } from '../../features/adaptive-learning/adaptiveUtils';

const TYPE_ICON = {
  REVIEW: LuRefreshCw,
  PRACTICE: LuZap,
  LEARN: LuBookOpen,
  FLASHCARDS: LuLayers,
};

export default function TodayLearningPage() {
  const { apiClient, userProfile } = useContext(AuthContext);
  const navigate = useNavigate();
  const [minutes, setMinutes] = useState(25);
  const { plan, loading, error, reload } = useTodayPlan(minutes);
  const [starting, setStarting] = useState(false);

  const items = plan?.items || [];
  const progressPct = useMemo(() => {
    if (!plan?.minutesBudget) return 0;
    return Math.min(100, Math.round((plan.totalEstimatedMinutes / plan.minutesBudget) * 100));
  }, [plan]);

  const startSession = async () => {
    setStarting(true);
    try {
      const res = await apiClient.post('/api/v1/learning/sessions', {
        source: 'TODAY',
        targetMinutes: minutes,
      });
      const first = items[0];
      const path = first?.linkPath || resolveLearningLink(res.data?.nextAction);
      navigate(path);
    } catch {
      navigate(items[0]?.linkPath || '/review');
    } finally {
      setStarting(false);
    }
  };

  return (
    <>
      <PageMeta title="Today's learning" description="Your personalized daily plan." />
      <AdaptivePageShell userProfile={userProfile} loading={loading && !plan}>
        <header className="adaptive-hero">
          <h1>Today&apos;s plan</h1>
          <p className="adaptive-hero-lead">
            Revision due, weak topics, and new material — sized to your time budget.
          </p>
          <div className="adaptive-time-presets" role="group" aria-label="Quick time presets">
            {TIME_PRESETS.map((m) => (
              <button
                key={m}
                type="button"
                className={`adaptive-time-chip${minutes === m ? ' adaptive-time-chip--active' : ''}`}
                onClick={() => setMinutes(m)}
              >
                {m} min
              </button>
            ))}
          </div>
          <label className="adaptive-minutes">
            <span>Custom</span>
            <input
              type="range"
              min={5}
              max={90}
              step={5}
              value={minutes}
              onChange={(e) => setMinutes(Number(e.target.value))}
              aria-valuetext={`${minutes} minutes`}
            />
            <span>{minutes} min</span>
          </label>
        </header>

        {error && (
          <p className="adaptive-error" role="alert">
            {error}
            <button type="button" className="learn-btn learn-btn--secondary" style={{ marginLeft: 8 }} onClick={reload}>
              Retry
            </button>
          </p>
        )}

        {loading && !plan && (
          <>
            <div className="adaptive-skeleton" />
            <div className="adaptive-skeleton" />
            <div className="adaptive-skeleton" />
          </>
        )}

        {plan && (
          <>
            <p className="adaptive-total">
              ~{plan.totalEstimatedMinutes} of {plan.minutesBudget} min planned
              {plan.dueCount > 0 && ` · ${plan.dueCount} reviews due`}
            </p>
            <div
              className="learn-progress learn-progress--lg"
              role="progressbar"
              aria-valuenow={progressPct}
              aria-valuemin={0}
              aria-valuemax={100}
              style={{ marginBottom: '1rem' }}
            >
              <span style={{ width: `${progressPct}%` }} />
            </div>

            <ol className="today-plan-list">
              {items.map((item, idx) => {
                const Icon = TYPE_ICON[item.type] || LuSparkles;
                return (
                  <li key={`${item.type}-${item.title}-${idx}`} className="today-plan-item">
                    <div className="today-plan-item-head">
                      <span className={`today-plan-item-icon today-plan-item-icon--${item.type}`} aria-hidden>
                        <Icon />
                      </span>
                      <div className="today-plan-item-body">
                        <span className={`action-badge action-badge--${(item.type || 'learn').toLowerCase()}`}>
                          {item.type}
                        </span>
                        <strong>{item.title}</strong>
                        <span className="adaptive-muted">{item.detail}</span>
                      </div>
                    </div>
                    <div className="today-plan-item-meta">
                      <span>
                        {item.estimatedMinutes} min
                        {item.questionCount ? ` · ${item.questionCount} items` : ''}
                      </span>
                      <Link to={item.linkPath || '/library'} className="today-plan-open">
                        Open →
                      </Link>
                    </div>
                  </li>
                );
              })}
            </ol>

            {items.length === 0 && (
              <div className="adaptive-empty">
                <p>No plan items for this budget. Try more time or add content to a notebook.</p>
                <Link to="/hub/notebooks/new" className="learn-btn learn-btn--primary">
                  Create notebook
                </Link>
              </div>
            )}

            <div className="adaptive-start-row">
              <button
                type="button"
                className="learn-btn learn-btn--primary adaptive-start-btn"
                disabled={starting || items.length === 0}
                onClick={startSession}
              >
                {starting ? 'Starting…' : "Start today's learning"}
              </button>
              <Link to="/mastery" className="learn-btn learn-btn-secondary">
                View knowledge map
              </Link>
            </div>
          </>
        )}
      </AdaptivePageShell>
    </>
  );
}
